import {
  FlexBox,
  ImageThumbnail,
  NavigatableWrapper,
} from '@airship-platform/jam-web/components';
import { BoxIcon } from '@airship-platform/jam-web/icons';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PropsWithChildren, useState } from 'react';

interface ManageProductSectionProps {
  product: {
    name: string;
    code?: string;
    model?: string;
    images: { url?: string; type?: string }[];
    error?: { title: string; details: string[] } | null;
  };
  showCodes?: boolean;
}

const ManageProductSection: React.FC<
  PropsWithChildren<ManageProductSectionProps>
> = ({ product, children, showCodes = true }) => {
  const availableImages = product.images.filter(
    (img) => img.url && img.type === 'Image'
  );

  const availableVideos = product.images.filter(
    (img) => img.url && img.type === 'Video'
  );

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const selectedImage = availableImages[selectedImageIndex];
  const hasMultipleImages = availableImages.length > 1;

  const renderError = () => {
    if (!product.error) {
      return null;
    }

    return (
      <Alert severity="error">
        <Typography variant="subtitle2" sx={{ display: 'block' }}>
          {product.error.title}
        </Typography>
        {product.error.details.map((detail, index) => (
          <Typography key={index} variant="body2">
            {detail}
          </Typography>
        ))}
      </Alert>
    );
  };

  return (
    <FlexBox flexDirection={['column']} width={'100%'} gap={3}>
      {renderError()}
      <FlexBox flexDirection={['column', 'row']} width={'100%'} gap={3}>
        <FlexBox flexDirection={'column'} gap={2}>
          <ImageThumbnail
            imageUrl={selectedImage?.url}
            alt={product.name}
            fallback={<BoxIcon />}
            hasBackground
            sx={{
              width: '12.5rem',
              height: '12.5rem',
              alignSelf: 'flex-start',
            }}
          />

          {hasMultipleImages && (
            <NavigatableWrapper sx={{ width: '12.5rem' }}>
              <FlexBox gap={1} sx={{ overflowX: 'auto', pb: 1 }}>
                {availableImages.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    sx={{
                      cursor: 'pointer',
                      border:
                        selectedImageIndex === index
                          ? '2px solid'
                          : '1px solid',
                      borderColor:
                        selectedImageIndex === index ? 'blue.500' : 'grey.300',
                      borderRadius: 1,
                      minWidth: '3rem',
                      height: '3rem',
                      overflow: 'hidden',
                    }}
                  >
                    <ImageThumbnail
                      imageUrl={image.url}
                      alt={`${product.name} - Image ${index + 1}`}
                      fallback={<BoxIcon />}
                      objectFit="cover"
                      sx={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                ))}
              </FlexBox>
            </NavigatableWrapper>
          )}
        </FlexBox>

        <FlexBox flexDirection={'column'} width={'100%'} gap={1}>
          <Typography variant="h2">{product.name}</Typography>
          {showCodes && product.code && (
            <Typography variant="body2">Code: {product.code}</Typography>
          )}
          {showCodes && product.model && (
            <Typography variant="body2">Model: {product.model}</Typography>
          )}
          {children}
          {!!availableVideos.length && (
            <FlexBox flexDirection={'column'} gap={2} width={'100%'}>
              {availableVideos.map((video) => (
                <Box
                  key={video.url}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <iframe
                    src={video.url}
                    title={`${product.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Box>
              ))}
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
};

export default ManageProductSection;
